import { useEffect, useState } from "react";
import { Sparkles, Newspaper, RefreshCcw, Tag, Eye, CheckSquare, X } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminAutoBlog() {
  const { toast } = useToast();
  const [drafts, setDrafts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [customTopic, setCustomTopic] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [previewSlug, setPreviewSlug] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selected, setSelected] = useState({});

  function load() {
    api.get("/admin/blog-posts").then((r) => setDrafts((r.data.posts || []).filter((p) => !p.published)));
    api.get("/admin/auto-blog/categories").then((r) => setCategories(r.data.categories || []));
    setSelected({});
  }
  useEffect(load, []);

  async function generate() {
    setGenerating(true);
    try {
      const payload = customTopic.trim() ? { custom_topic: customTopic.trim() } : {};
      const r = await api.post("/admin/auto-blog/generate-now", payload);
      toast({ title: "Concept aangemaakt", description: `${r.data.slug} · ${r.data.quality_flags?.length || 0} flags` });
      setCustomTopic("");
      setShowCustom(false);
      load();
    } catch (err) {
      toast({ title: "Generatie mislukt", description: err?.response?.data?.detail || "Probeer opnieuw", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  }

  async function publishClean() {
    const r = await api.post("/admin/auto-blog/publish-clean-drafts");
    toast({ title: `${r.data.published} drafts gepubliceerd` });
    load();
  }

  async function bulkPublishSelected() {
    const slugs = Object.entries(selected).filter(([, v]) => v).map(([s]) => s);
    if (slugs.length === 0) {
      toast({ title: "Niets geselecteerd", variant: "destructive" });
      return;
    }
    const r = await api.post("/admin/auto-blog/bulk-publish", { slugs });
    toast({ title: `${r.data.published} drafts gepubliceerd` });
    load();
  }

  async function openPreview(slug) {
    setPreviewSlug(slug);
    setPreviewData(null);
    const r = await api.get(`/admin/auto-blog/preview/${slug}`);
    setPreviewData(r.data);
  }

  async function publishFromPreview() {
    if (!previewSlug) return;
    const post = previewData;
    await api.put(`/admin/blog-posts/${previewSlug}`, { ...post, published: true });
    toast({ title: "Gepubliceerd" });
    setPreviewSlug(null);
    load();
  }

  async function saveCategory(slug) {
    const cat = categories.find((c) => c.slug === slug);
    await api.put(`/admin/auto-blog/categories/${slug}`, cat);
    toast({ title: "Categorie opgeslagen" });
  }

  async function publishDraft(slug) {
    const post = drafts.find((p) => p.slug === slug);
    await api.put(`/admin/blog-posts/${slug}`, { ...post, published: true });
    toast({ title: "Gepubliceerd" });
    load();
  }

  const CATS = [
    { slug: "wonen", label: "Wonen" },
    { slug: "werk", label: "Werk" },
    { slug: "financieel", label: "Financieel" },
    { slug: "consument", label: "Consument" },
    { slug: "digitaal", label: "Digitaal" },
    { slug: "gezin", label: "Gezin" },
    { slug: "vastgoed", label: "Vastgoed" },
  ];

  function setCatField(slug, key, value) {
    setCategories((prev) => {
      const existing = prev.find((c) => c.slug === slug);
      if (existing) {
        return prev.map((c) => c.slug === slug ? { ...c, [key]: value } : c);
      }
      return [...prev, { slug, label: CATS.find((x) => x.slug === slug)?.label || slug, [key]: value }];
    });
  }

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <AdminLayout>
      <div data-testid="admin-autoblog-page" className="space-y-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Auto-blog</h1>
            <p className="text-ink-soft mt-1 text-sm">Dagelijks 1 concept om 9:30 (Europe/Brussels). Concepten zonder quality-flags worden na X dagen automatisch gepubliceerd om 11:00.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowCustom((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-ink text-sm font-semibold"
              data-testid="toggle-custom-topic"
            >
              {showCustom ? "Sluit override" : "Custom prompt"}
            </button>
            <button
              onClick={generate}
              disabled={generating}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold disabled:opacity-50"
              data-testid="generate-now"
            >
              <Sparkles className="h-4 w-4" /> {generating ? "Bezig (Claude)…" : "Genereer nu"}
            </button>
            <button
              onClick={publishClean}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              data-testid="publish-clean"
            >
              <RefreshCcw className="h-4 w-4" /> Publiceer cleane drafts
            </button>
          </div>
        </div>

        {showCustom && (
          <section className="rounded-2xl bg-blue-50 border border-blue-200 p-5">
            <Label htmlFor="custom-topic">Custom topic — formaat: <code className="bg-white px-1.5 py-0.5 rounded text-xs">Branche|Stad|Invalshoek</code></Label>
            <Input
              id="custom-topic"
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="HuurCheck|Gent|3 cluasules die studenten in Gent over het hoofd zien"
              data-testid="custom-topic-input"
              className="mt-2 bg-white"
            />
            <p className="text-xs text-ink-soft mt-2">Wordt enkel gebruikt voor deze ene "Genereer nu"-klik. Laat leeg om random uit de topic-pool te kiezen.</p>
          </section>
        )}

        {/* Categorieën */}
        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2"><Tag className="h-5 w-5 text-[#002FA7]" /> Blog categorieën — thumbnail per cat</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {CATS.map((c) => {
              const stored = categories.find((x) => x.slug === c.slug) || {};
              return (
                <div key={c.slug} className="rounded-xl border border-border p-4 flex items-center gap-4">
                  <div className="h-14 w-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {stored.thumbnail_url && <img src={stored.thumbnail_url} alt={c.label} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-ink-soft font-semibold">{c.label}</div>
                    <Input
                      value={stored.thumbnail_url || ""}
                      onChange={(e) => setCatField(c.slug, "thumbnail_url", e.target.value)}
                      placeholder="https://… thumbnail URL"
                      className="mt-1 text-xs"
                    />
                  </div>
                  <button onClick={() => saveCategory(c.slug)} className="text-xs font-semibold text-[#002FA7] hover:underline">Bewaar</button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Drafts */}
        <section className="rounded-2xl bg-white border border-border p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-[#002FA7]" /> Concept review ({drafts.length})
            </h2>
            {selectedCount > 0 && (
              <button
                onClick={bulkPublishSelected}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                data-testid="bulk-publish-selected"
              >
                <CheckSquare className="h-4 w-4" /> Publiceer {selectedCount} geselecteerd
              </button>
            )}
          </div>
          <div className="mt-4 space-y-3">
            {drafts.length === 0 && <p className="text-ink-soft text-sm">Geen drafts in de wachtrij. Klik 'Genereer nu' om er één te maken.</p>}
            {drafts.map((p) => (
              <div key={p.slug} className="rounded-xl border border-border p-4 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <input
                    type="checkbox"
                    checked={!!selected[p.slug]}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [p.slug]: e.target.checked }))}
                    className="mt-1.5"
                    data-testid={`draft-select-${p.slug}`}
                  />
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-ink truncate">{p.title}</h3>
                    <p className="text-xs text-ink-subtle mt-1 truncate">{p.slug} · {p.word_count || "?"} woorden · category: {p.category || "?"}</p>
                    {p.quality_flags?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {p.quality_flags.slice(0, 5).map((f) => (
                          <span key={f} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => openPreview(p.slug)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 text-ink text-xs font-semibold" data-testid={`preview-${p.slug}`}>
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </button>
                  <button onClick={() => publishDraft(p.slug)} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold">Publiceer</button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Preview modal */}
        {previewSlug && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreviewSlug(null)} data-testid="preview-modal">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between">
                <h3 className="font-display font-semibold text-ink">Preview: {previewSlug}</h3>
                <div className="flex gap-2">
                  <button onClick={publishFromPreview} className="px-4 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">Publiceer nu</button>
                  <button onClick={() => setPreviewSlug(null)} className="p-1.5 hover:bg-slate-100 rounded-full"><X className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="p-6">
                {!previewData ? (
                  <div className="h-8 w-1/2 bg-slate-200 rounded animate-pulse" />
                ) : (
                  <>
                    <h1 className="font-display text-3xl font-bold text-ink">{previewData.title}</h1>
                    <p className="text-sm text-ink-soft mt-2">{previewData.meta_description}</p>
                    {previewData.image_url && <img src={previewData.image_url} alt="" className="w-full h-56 object-cover rounded-xl mt-5" />}
                    <article className="prose prose-slate max-w-none mt-6" dangerouslySetInnerHTML={{ __html: previewData.body_html || "" }} />
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
